import { OdinIcon } from '@myosh/odin-components';
import type { MouseEvent, ReactNode } from 'react';
import { useEffect, useId, useMemo, useState } from 'react';
import { cn, makeDataCy } from '../../helpers/util';

interface AccordionProps {
  title: string;
  children: ReactNode;
  isOpen?: boolean;
  onClick?: (isOpen: boolean) => void;
}

function Accordion({ title, children, isOpen, onClick }: AccordionProps) {
  const contentWrapperId = useId();
  const [open, setOpen] = useState(isOpen);
  const dataCyTitle = useMemo(() => makeDataCy(title), [title]);

  const onAccordionClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    onClick?.(!open);
    setOpen(!open);
  };

  useEffect(() => {
    if (isOpen !== open) {
      setOpen(isOpen);
    }
  }, [isOpen]);

  return (
    <div className="mb-4 flex w-full flex-col">
      <a
        data-cy={`accordion-toggle-${dataCyTitle}`}
        href={`#${contentWrapperId}`}
        className={
          'border-gray-2 focus:border-primary-3 flex border-b border-solid focus:-mb-0.5 focus:border-b-2 focus:outline-hidden'
        }
        onClick={onAccordionClick}
      >
        <div className="grow text-sm leading-9 font-bold">{title}</div>
        <div className="flex shrink items-center pr-px">
          <OdinIcon icon={open ? 'ChevronUp' : 'ChevronDown'} />
        </div>
      </a>
      <div
        id={contentWrapperId}
        data-cy={`accordion-content-${dataCyTitle}`}
        className={cn('grow pt-5', { hidden: !open })}
      >
        {children}
      </div>
    </div>
  );
}

export default Accordion;
